import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

import HelpKitKnowledgeBase from '../general/HelpKitKnowledgeBase.vue'
import { reactive, onMounted, nextTick, watch } from 'vue'
import {
  PopoverActionType,
  PopoverActionEvent,
  SectionChangeData,
} from '../general/types'
import { MJPSection, SectionSequence } from './types'
import { useEventBus } from '@vueuse/core'
import useLanguage from '@/composables/useLanguage'
import MediaLoader from './MediaLoader.vue'
import { useRoute, useRouter } from 'vue-router'
import AudioManager from '../general/AudioManager.vue'
import OverheadMap from './OverheadMap.vue'
import VideoPlayer from '../general/VideoPlayer.vue'
import TranscriptViewer from '../general/TranscriptViewer.vue'
import MainNavigation from './MainNavigation.vue'
import SectionWrapper from '../general/SectionWrapper.vue'
import hotspotData from '@/assets/data/mjp/hotspot-data.js'
import sectionAudio from '@/assets/data/mjp/section-audio.js'
import useSectionData from '@/composables/mjp/useSectionData'
import SettingsScreen from '../general/SettingsScreen.vue'

/*
 * root component of the explorable
 * also contains all interface elements
 */

interface MagicJohnsonParkState {
  isBlurred: boolean
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  isMovingForward: boolean
  lastSection: MJPSection
  currentPage: number
  activeHotspotId: string
  hoveredHotspotId: string
  isMuted: boolean
  settingsAreOpen: boolean
  prefersReducedMotion: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MagicJohnsonPark',
  setup(__props) {

const sectionChangedBus = useEventBus('section-changed')
const sectionLoadedBus = useEventBus('section-loaded')
const interfaceText = useLanguage('interface')
const router = useRouter()
const route = useRoute()
const videoPlayerOpenedBus = useEventBus('video-opened')
const videoPlayerClosedBus = useEventBus('video-closed')
const popoverActionTriggeredBus = useEventBus('popover-action-triggered')
const hotspotClickedBus = useEventBus('hotspot-clicked')
const hotspotHoveredBus = useEventBus('hotspot-hovered')
const popoverClosedBus = useEventBus('popover-closed')
const popoversNeedUpdateBus = useEventBus('popovers-need-update')
const setAudioMutedBus = useEventBus('set-audio-muted')
const settingsOpenedBus = useEventBus('settings-opened')
const settingsClosedBus = useEventBus('settings-closed')
const transcriptOpenedBus = useEventBus('transcript-opened')
const transcriptClosedBus = useEventBus('transcript-closed')
const state: MagicJohnsonParkState = reactive({
  isBlurred: false,
  currentSection: MJPSection.None,
  currentSectionHasLoaded: false,
  isMovingForward: true,
  lastSection: MJPSection.None,
  currentPage: -1,
  activeHotspotId: '',
  hoveredHotspotId: '',
  isMuted: true,
  settingsAreOpen: false,
  prefersReducedMotion: matchMedia('(prefers-reduced-motion)').matches,
})

setAudioMutedBus.on((isMuted: unknown) => {
  state.isMuted = isMuted as boolean
})

sectionChangedBus.on((sectionData: unknown) => {
  const sectionDataProxy = sectionData as SectionChangeData
  if (sectionDataProxy.section != state.currentSection) {
    state.currentSectionHasLoaded = false
    state.currentSection = sectionDataProxy.section
    router.push({
      name: 'after-the-rain',
      params: { section: state.currentSection },
    })
  }
  state.activeHotspotId = ''
  state.hoveredHotspotId = ''
  state.lastSection = sectionDataProxy.lastSection
  state.currentPage = sectionDataProxy.pageNumber
  state.isMovingForward = sectionDataProxy.forward
})

const setInitialFocus = () => {
  const sectionData = useSectionData(state.currentSection)
  if (sectionData.initialFocus) {
    const elementId = sectionData.initialFocus
    setTimeout(() => {
      const link = document.getElementById(elementId)
      link?.focus()
    }, 1000)
  } else {
    const element = document.activeElement as HTMLElement
    element.blur()
  }
}

sectionLoadedBus.on(() => {
  //setInitialFocus()
  state.currentSectionHasLoaded = true
})

popoverActionTriggeredBus.on((data: unknown) => {
  const dataProxy = data as PopoverActionEvent
  let rect, info, target
  switch (dataProxy.action.type) {
    case PopoverActionType.GoToSection:
      sectionChangedBus.emit({
        section: dataProxy.action.destination,
        pageNumber: 0,
        forward: true,
        lastSection: state.currentSection,
      })
      break
    case PopoverActionType.PlayVideo:
      target = dataProxy.event.currentTarget as HTMLElement
      rect = target.getBoundingClientRect()
      info = {
        x: rect.x + rect.width * 0.5,
        y: rect.y + rect.height * 0.5,
        width: dataProxy.action.width,
        height: dataProxy.action.height,
        filename: dataProxy.action.content,
        videoUID: dataProxy.action.videoUID,
      }
      videoPlayerOpenedBus.emit(info)
      break
  }
})

hotspotClickedBus.on((index) => {
  if (
    index == -1 ||
    state.activeHotspotId == hotspotData[state.currentSection][index].id
  ) {
    state.activeHotspotId = ''
  } else {
    state.activeHotspotId = hotspotData[state.currentSection][index].id
  }
  state.hoveredHotspotId = ''
  nextTick(() => {
    popoversNeedUpdateBus.emit()
  })
})

hotspotHoveredBus.on((index) => {
  if (index == -1) {
    state.hoveredHotspotId = ''
  } else {
    state.hoveredHotspotId = hotspotData[state.currentSection][index].id
    if (state.hoveredHotspotId != state.activeHotspotId) {
      state.activeHotspotId = ''
    }
  }
  nextTick(() => {
    popoversNeedUpdateBus.emit()
  })
})

popoverClosedBus.on(() => {
  state.activeHotspotId = ''
  popoversNeedUpdateBus.emit()
})

videoPlayerOpenedBus.on((info) => {
  state.isBlurred = true
})

videoPlayerClosedBus.on(() => {
  state.isBlurred = false
})

settingsOpenedBus.on(() => {
  state.settingsAreOpen = true
})

settingsClosedBus.on(() => {
  state.settingsAreOpen = false
})

transcriptOpenedBus.on((data) => {
  state.isBlurred = true
})

transcriptClosedBus.on(() => {
  state.isBlurred = false
})

const setupSignupForm = () => {
  let tallyScript = document.createElement('script')
  tallyScript.setAttribute('src', 'https://tally.so/widgets/embed.js')
  document.head.appendChild(tallyScript)
}

onMounted(() => {
  const section = route.params.section
  if (Object.values(MJPSection).includes(section as MJPSection)) {
    sectionChangedBus.emit({
      section: route.params.section,
      pageNumber: -1,
      forward: true,
      lastSection: state.currentSection,
    })
  } else {
    sectionChangedBus.emit({
      section: MJPSection.Title,
      pageNumber: -1,
      forward: true,
      lastSection: state.currentSection,
    })
  }
  setupSignupForm()
  //setInitialFocus()
  watch(
    () => route.params.section,
    async (newSection) => {
      if (newSection != state.currentSection) {
        sectionChangedBus.emit({
          section: newSection,
          pageNumber: -1,
          forward: true,
          lastSection: state.currentSection,
        })
      }
    }
  )
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      id: "magic-johnson-park",
      class: _normalizeClass(["absolute left-0 top-0 w-full h-full overflow-hidden", {
      'blur-lg': state.isBlurred,
    }])
    }, [
      _createVNode(AudioManager, {
        sectionAudio: _unref(sectionAudio),
        channels: 4
      }, null, 8, ["sectionAudio"]),
      _createElementVNode("main", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(SectionSequence), (sectionData, index) => {
          return (_openBlock(), _createBlock(SectionWrapper, {
            key: index,
            thisSection: sectionData.section
          }, {
            default: _withCtx(() => [
              (
            state.currentSection == sectionData.section ||
            (state.lastSection == sectionData.section &&
              !state.currentSectionHasLoaded)
          )
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(sectionData.component), {
                    key: 0,
                    sectionData: sectionData,
                    currentSection: state.currentSection,
                    currentSectionHasLoaded: state.currentSectionHasLoaded,
                    currentPage: state.currentPage,
                    lastSection: state.lastSection,
                    forward: state.isMovingForward,
                    activeHotspotId: state.activeHotspotId,
                    hoveredHotspotId: state.hoveredHotspotId,
                    prefersReducedMotion: state.prefersReducedMotion
                  }, null, 8, ["sectionData", "currentSection", "currentSectionHasLoaded", "currentPage", "lastSection", "forward", "activeHotspotId", "hoveredHotspotId", "prefersReducedMotion"]))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1032, ["thisSection"]))
        }), 128)),
        _createVNode(OverheadMap, {
          currentSection: state.currentSection,
          currentSectionHasLoaded: state.currentSectionHasLoaded,
          currentPage: state.currentPage,
          settingsAreOpen: state.settingsAreOpen
        }, null, 8, ["currentSection", "currentSectionHasLoaded", "currentPage", "settingsAreOpen"])
      ]),
      _createVNode(SettingsScreen, {
        isOpen: state.settingsAreOpen
      }, null, 8, ["isOpen"]),
      _createVNode(MainNavigation, {
        currentSection: state.currentSection,
        currentSectionHasLoaded: state.currentSectionHasLoaded,
        currentPage: state.currentPage,
        lastSection: state.lastSection,
        isMuted: state.isMuted,
        settingsAreOpen: state.settingsAreOpen
      }, null, 8, ["currentSection", "currentSectionHasLoaded", "currentPage", "lastSection", "isMuted", "settingsAreOpen"]),
      _createVNode(MediaLoader, {
        currentSection: state.currentSection,
        currentSectionHasLoaded: state.currentSectionHasLoaded,
        animationPath: 
        state.prefersReducedMotion
          ? '/images/mjp/loading-cropped.png'
          : '/images/mjp/loading-cropped.gif'
      ,
        altText: _unref(interfaceText).loaderAnimationAltText,
        classes: "w-[300px]"
      }, null, 8, ["currentSection", "currentSectionHasLoaded", "animationPath", "altText"])
    ], 2),
    _createVNode(VideoPlayer),
    _createVNode(TranscriptViewer),
    _createVNode(HelpKitKnowledgeBase)
  ], 64))
}
}

})